import { ok } from '@orangelv/utils'

const DEFAULT_PARAMS = {
  displacementStrength: 1.5, // Controls how pronounced height changes appear
  sampleRadius: 2, // How far to look for height differences
  transitionPoint: 0.95, // Creates gradual blend between the calculated normal and the default "flat" normal
  blurRadius: 6, // Blur radius for smoothing the height map
  alphaThreshold: 20, // Threshold for alpha to determine content vs background
  depthFactor: 0.9, // Controls depth of the normal map, should be < 1
}

type NormalMapParams = typeof DEFAULT_PARAMS

type Dimensions = {
  width: number
  height: number
}

// Normal vector components in 3D space
type Normal = {
  dx: number
  dy: number
  dz: number
}

// Color channels in the normal map texture
type NormalRGB = {
  x: number
  y: number
  z: number
}

const canvasPool: HTMLCanvasElement[] = []
const MAX_POOL_SIZE = 3

const getCanvas = (width: number, height: number): HTMLCanvasElement => {
  const canvas = canvasPool.pop() || document.createElement('canvas')
  canvas.width = width
  canvas.height = height

  return canvas
}

const releaseCanvas = (canvas: HTMLCanvasElement): void => {
  const ctx = canvas.getContext('2d')
  if (ctx) {
    ctx.clearRect(0, 0, canvas.width, canvas.height)
  }

  if (canvasPool.length < MAX_POOL_SIZE) {
    canvasPool.push(canvas)
  }
}

const loadImage = (src: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    if (!src) {
      reject(new Error('Invalid image source'))
      return
    }

    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.onload = () => resolve(img)
    img.onerror = () => reject(new Error(`Failed to load image: ${src}`))
    img.onabort = () => reject(new Error('Image loading was aborted'))
    img.src = src
  })

const getFittedDimensions = (
  imgWidth: number,
  imgHeight: number,
  targetDimension: number,
): Dimensions => {
  const aspectRatio = imgWidth / imgHeight

  if (imgHeight > imgWidth) {
    return {
      width: Math.round(targetDimension * aspectRatio),
      height: targetDimension,
    }
  }

  return {
    width: targetDimension,
    height: Math.round(targetDimension / aspectRatio),
  }
}

const applyBlur = (
  ctx: CanvasRenderingContext2D,
  imageData: ImageData,
  radius: number,
): ImageData => {
  if (!ctx || !imageData || radius <= 0) {
    return imageData
  }

  const tempCanvas = getCanvas(imageData.width, imageData.height)
  const tempCtx = tempCanvas.getContext('2d')

  if (!tempCtx) {
    console.warn('Could not get context for blur operation')
    releaseCanvas(tempCanvas)
    return imageData
  }

  tempCtx.putImageData(imageData, 0, 0)

  ctx.clearRect(0, 0, imageData.width, imageData.height)
  ctx.filter = `blur(${radius}px)`
  ctx.drawImage(tempCanvas, 0, 0)
  ctx.filter = 'none'

  const result = ctx.getImageData(0, 0, imageData.width, imageData.height)
  releaseCanvas(tempCanvas)
  return result
}

const getHeightMapData = (
  source: HTMLImageElement | HTMLCanvasElement,
  width: number,
  height: number,
  params: NormalMapParams,
): Uint8Array => {
  const { blurRadius, alphaThreshold, depthFactor } = params
  const canvas = getCanvas(width, height)
  const ctx = canvas.getContext('2d')

  ok(ctx, 'Failed to get canvas context')
  ctx.clearRect(0, 0, width, height)

  // If blur is needed, apply it directly to the source when drawing
  if (blurRadius > 0) {
    ctx.filter = `blur(${blurRadius}px)`
    ctx.drawImage(source, 0, 0, width, height)
    ctx.filter = 'none'
  } else {
    ctx.drawImage(source, 0, 0, width, height)
  }

  const imageData = ctx.getImageData(0, 0, width, height)
  const sourceData = imageData.data
  const heightMap = new Uint8Array(width * height)

  // Generate height map based on alpha values
  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      const i = (y * width + x) * 4
      const alpha = sourceData[i + 3]

      if (alpha > alphaThreshold) {
        heightMap[y * width + x] = Math.round(255 - alpha * depthFactor)
      } else {
        heightMap[y * width + x] = 255 // Maximum height for transparent areas
      }
    }
  }

  releaseCanvas(canvas)
  return heightMap
}

const calculateNormalRGB = (
  x: number,
  y: number,
  width: number,
  height: number,
  heightMap: Uint8Array,
  params: NormalMapParams,
): NormalRGB & { heightValue: number } => {
  const { displacementStrength, sampleRadius } = params
  const idx = y * width + x

  // Get height value and normalize to 0-1 range
  const heightValue = heightMap[idx] / 255

  // Sample heights from neighboring pixels with bounds checking
  const leftIdx = x > sampleRadius ? y * width + (x - sampleRadius) : idx
  const rightIdx =
    x < width - sampleRadius ? y * width + (x + sampleRadius) : idx
  const topIdx = y > sampleRadius ? (y - sampleRadius) * width + x : idx
  const bottomIdx =
    y < height - sampleRadius ? (y + sampleRadius) * width + x : idx

  const left = heightMap[leftIdx] / 255
  const right = heightMap[rightIdx] / 255
  const top = heightMap[topIdx] / 255
  const bottom = heightMap[bottomIdx] / 255

  // Calculate normal vector components from height differences
  const dx = (right - left) * displacementStrength
  const dy = (bottom - top) * displacementStrength

  // Ensure the normal points somewhat upward for a realistic effect
  const dz = Math.sqrt(1.0 - Math.min(0.99, dx * dx + dy * dy))

  // Normalize the vector
  const length = Math.sqrt(dx * dx + dy * dy + dz * dz) || 0.0001

  // Map normal vector to RGB color space (with inversion for debossing)
  return {
    x: Math.round(((-dx / length) * 0.5 + 0.5) * 255),
    y: Math.round(((-dy / length) * 0.5 + 0.5) * 255),
    z: Math.round(((dz / length) * 0.5 + 0.5) * 255),
    heightValue,
  }
}

export const createNormalMapFromImage = async (
  imageSource:
    | HTMLImageElement
    | string
    | {
        getCanvas: (
          canvas: HTMLCanvasElement,
        ) => Promise<HTMLCanvasElement | undefined>
      }
    | undefined,
  outputCanvas: HTMLCanvasElement,
  targetDimension = 1024,
  customParams: Partial<NormalMapParams> = {},
): Promise<HTMLCanvasElement> => {
  ok(imageSource, `Bad image source: ${imageSource}`)

  try {
    const params = { ...DEFAULT_PARAMS, ...customParams }
    let imageData: HTMLImageElement | HTMLCanvasElement | undefined

    if (typeof imageSource === 'string') {
      imageData = await loadImage(imageSource)
    } else if (typeof imageSource === 'object' && 'getCanvas' in imageSource) {
      const canvas = getCanvas(targetDimension, targetDimension)
      await imageSource.getCanvas(canvas)
      imageData = canvas
    } else {
      imageData = imageSource
    }

    const { width, height } = getFittedDimensions(
      imageData.width,
      imageData.height,
      targetDimension,
    )

    if (outputCanvas.width !== width || outputCanvas.height !== height) {
      outputCanvas.width = width
      outputCanvas.height = height
    }

    const heightMap = getHeightMapData(imageData, width, height, params)

    if (imageData instanceof HTMLCanvasElement) {
      releaseCanvas(imageData)
    }

    const ctx = outputCanvas.getContext('2d')
    ok(ctx, 'Failed to get output canvas context')

    const normalMapData = ctx.createImageData(width, height)
    const normalMap = normalMapData.data

    const flatNormalX = 128
    const flatNormalY = 128
    const flatNormalZ = 255

    for (let y = 0; y < height; y += 1) {
      for (let x = 0; x < width; x += 1) {
        const pixelIndex = y * width + x
        const outputIndex = pixelIndex * 4

        const rawHeightValue = heightMap[pixelIndex]
        // Normalize height value (0-1)
        const heightValue = rawHeightValue / 255

        // Calculate transition factor (how much to deboss)
        const transitionFactor = Math.min(
          1,
          Math.max(0, (params.transitionPoint - heightValue) * 10),
        )

        if (transitionFactor > 0) {
          const normal = calculateNormalRGB(
            x,
            y,
            width,
            height,
            heightMap,
            params,
          )

          normalMap[outputIndex] = Math.round(
            normal.x * transitionFactor + flatNormalX * (1 - transitionFactor),
          )
          normalMap[outputIndex + 1] = Math.round(
            normal.y * transitionFactor + flatNormalY * (1 - transitionFactor),
          )
          normalMap[outputIndex + 2] = Math.round(
            normal.z * transitionFactor + flatNormalZ * (1 - transitionFactor),
          )
        } else {
          normalMap[outputIndex] = flatNormalX
          normalMap[outputIndex + 1] = flatNormalY
          normalMap[outputIndex + 2] = flatNormalZ
        }

        normalMap[outputIndex + 3] = rawHeightValue
      }
    }

    ctx.putImageData(normalMapData, 0, 0)
    return outputCanvas
  } catch (error) {
    console.error('Error generating normal map:', error)
    throw error
  }
}
